// src/components/student/StudentPreviewRoute.js
//
// Pass 82D §20–§28 + Pass 85.1A §14 — the authorized-staff Student Preview. It NEVER changes the signed-in operator's
// identity: a separate viewer context that renders the REAL student experience (StudentExperience) read-only for an
// EXPLICITLY selected CANONICAL PARTICIPANT, scoped to an organization the staff owns/administers. Candidate discovery is
// now the canonical `participantProfiles` (the coached human) — NOT Auth-uid ownership of videos/lessons. A participant
// with NO videos/lessons is still previewable (identity is the profile). If no participant exists (production today has
// none) it says so honestly and never fabricates one (§38). Zero business writes (§36).

import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { canPreviewStudentWorkspace, ORG_MANAGE_ROLES } from '../../utils/platformAccess';
import { getOrganizationMemberships } from '../../utils/organizationContext';
import { loadParticipantPreviewCandidates } from '../../utils/studentPreviewCandidates';
import { loadParticipantVideos, loadParticipantLessonProjections } from '../../utils/studentDataAccess';
import StudentExperience from './StudentExperience';

const C = { bg: '#FAFAF8', card: '#FFFFFF', border: 'rgba(0,0,0,0.08)', blue: '#2563EB', t1: '#1C1B18', t2: '#57564F', t3: '#8A8980', red: '#DC2626' };
const font = "'DM Sans',system-ui,sans-serif";

export default function StudentPreviewRoute() {
  const { userData } = useAuth() || {};
  // Orgs this staff member OWNS/ADMINISTERS — the only orgs whose participants they may preview (§22). Pass 87.1 —
  // carry the HUMAN organisation name so the dropdown + empty-state never show a raw id (e.g. "motus-pilot-2"); the
  // id stays the stable internal value/key. `organizationName` is attached to memberships by AuthContext.
  const previewableOrgs = useMemo(
    () => getOrganizationMemberships(userData)
      .filter((m) => ORG_MANAGE_ROLES.includes(m.role))
      .map((m) => ({ organizationId: m.organizationId, organizationName: m.organizationName || m.organizationId })),
    [userData],
  );
  const orgNameFor = (id) => (previewableOrgs.find((o) => o.organizationId === id)?.organizationName) || id || 'this organisation';
  const [orgId, setOrgId] = useState(previewableOrgs[0]?.organizationId || userData?.orgId || '');
  const [candidates, setCandidates] = useState(null);
  const [selected, setSelected] = useState(null); // { participantId, name }
  const [health, setHealth] = useState(null);
  const [loadErr, setLoadErr] = useState('');

  useEffect(() => { if (!orgId && previewableOrgs[0]) setOrgId(previewableOrgs[0].organizationId); }, [previewableOrgs, orgId]);

  // Load canonical participant candidates for the selected org (read-only). Reset selection on org change (no stale bleed).
  useEffect(() => {
    let alive = true;
    setSelected(null); setHealth(null); setCandidates(null); setLoadErr('');
    if (!orgId) { setCandidates([]); return undefined; }
    (async () => {
      try {
        const base = await loadParticipantPreviewCandidates(db, orgId);
        if (alive) setCandidates(base);
      } catch (e) { if (alive) { setLoadErr('Could not load participants for this organisation.'); setCandidates([]); } }
    })();
    return () => { alive = false; };
  }, [orgId]);

  // Build a modern DATA CHECKS summary for the selected participant (read-only, org+participant scoped).
  useEffect(() => {
    let alive = true;
    setHealth(null);
    if (!selected || !orgId) return undefined;
    (async () => {
      try {
        const [videos, lessons] = await Promise.all([
          loadParticipantVideos(db, { participantId: selected.participantId, organizationId: orgId }),
          loadParticipantLessonProjections(db, { participantId: selected.participantId, organizationId: orgId }),
        ]);
        if (alive) setHealth({ organizationId: orgId, participantId: selected.participantId, counts: { videos: videos.length, lessons: lessons.length }, accountLinked: !!selected.accountLinked });
      } catch (e) { if (alive) setHealth({ error: true, organizationId: orgId, participantId: selected.participantId }); } // error ≠ empty (scope-tagged)
    })();
    return () => { alive = false; };
  }, [selected, orgId]);

  if (!canPreviewStudentWorkspace(userData || {}, orgId)) {
    return <div data-testid="student-preview-denied" style={{ fontFamily: font, padding: 32, color: C.t2 }}>Student Preview is available to organisation owners and admins only.</div>;
  }

  return (
    <div data-testid="student-preview-route" style={{ fontFamily: font, color: C.t1, background: C.bg, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 24px 60px' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>Student Preview</h2>
        <div style={{ fontSize: 12.5, color: C.t3, marginBottom: 16 }}>Inspect a coaching participant’s real experience read-only — for QA. This does not change who you are signed in as.</div>

        {/* Org + participant selectors */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
          {previewableOrgs.length > 1 && (
            <select data-testid="preview-org-select" value={orgId} onChange={(e) => setOrgId(e.target.value)} style={sel}>
              {previewableOrgs.map((o) => <option key={o.organizationId} value={o.organizationId}>{o.organizationName}</option>)}
            </select>
          )}
          <select data-testid="preview-student-select" value={selected?.participantId || ''} onChange={(e) => {
            const c = (candidates || []).find((x) => x.participantId === e.target.value);
            setSelected(c ? { participantId: c.participantId, name: c.name, accountLinked: c.accountLinked } : null);
          }} style={sel} disabled={!candidates || candidates.length === 0}>
            <option value="">{candidates === null ? 'Loading participants…' : candidates.length === 0 ? 'No participants' : 'Select a participant…'}</option>
            {(candidates || []).map((c) => <option key={c.participantId} value={c.participantId}>{c.name}{c.primaryDiscipline ? ` · ${c.primaryDiscipline}` : ''}{c.accountLinked ? '' : ' · (no login yet)'}</option>)}
          </select>
        </div>

        {loadErr && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{loadErr}</div>}

        {/* Honest empty state (§38) — never fabricate a participant */}
        {candidates !== null && candidates.length === 0 && !loadErr && (
          <div data-testid="preview-no-students" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, fontSize: 13, color: C.t2 }}>
            No coaching participant is currently available to preview in <strong>{orgNameFor(orgId)}</strong>. Participants are created in the client CRM; a participant becomes previewable as soon as their profile exists (they do not need a login yet).
          </div>
        )}

        {selected && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 16, alignItems: 'start' }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
              <StudentExperience participantId={selected.participantId} organizationId={orgId} studentName={selected.name} viewerMode="staff_preview" />
            </div>
            {/* DATA CHECKS — staff-only QA (§28/§29). Restrained; ids under a diagnostics line only. */}
            <div data-testid="preview-data-health" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, fontSize: 12.5 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Data checks</div>
              {/* Pass 85.1A.1 §9 — suppress a STALE health panel synchronously: only show it when it matches the CURRENTLY
                  selected participant + org, so a participant/org switch never flashes the prior participant's counts. */}
              {!health || health.participantId !== selected.participantId || health.organizationId !== orgId ? <div style={{ color: C.t3 }}>Loading…</div> : health.error ? <div style={{ color: C.red }}>Could not load this participant’s data.</div> : (
                <>
                  <Kv k="Organization" v={health.organizationId || '—'} />
                  <Kv k="Videos" v={health.counts.videos} />
                  <Kv k="Shared lessons" v={health.counts.lessons} />
                  <Kv k="Account linked" v={health.accountLinked ? 'Yes' : 'No (invite pending)'} />
                  <details style={{ marginTop: 8, color: C.t3 }}><summary style={{ cursor: 'pointer' }}>Diagnostics</summary><div style={{ wordBreak: 'break-all', marginTop: 4 }}>participantId: {health.participantId}</div></details>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const sel = { padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.12)', fontSize: 13, background: '#fff', fontFamily: font };
function Kv({ k, v }) { return <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}><span style={{ color: '#8A8980' }}>{k}</span><span style={{ fontWeight: 600 }}>{v}</span></div>; }
